import React, { useEffect, useState, useContext } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  TextField,
  Button,
  Typography
} from "@mui/material";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";
import {
  getArmyPickerArmies,
  getGameSystems,
  handlePagingAll
} from "../../components/api/APIUtilities";
import GameSystemPicker from "../../components/utilities/gamesystempickers/GameSystemPicker";
import GenericPicker from "../../components/utilities/GenericPicker";
import ArmyPicker from "../../components/utilities/ArmyPicker";
import { getData, postData } from "../../components/utilities/UtilityFunctions";
import ScorecardPicker from "../../components/utilities/ScorecardPicker";

export default function CasualDetails() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystem, setGameSystem] = useState(null);

  const [scorecards, setScorecards] = useState(null);

  const [armies, setArmies] = useState(null);
  const [armyDict, setArmyDict] = useState(["Loading..."]);
  const [hideFactions, setHideFactions] = useState(false);

  useEffect(() => {
    const go = async () => {
      try {
        let apiGameSystems = await getGameSystems();

        apiGameSystems = apiGameSystems.filter(
          (gameSystem) => !gameSystem?.boardGameEvent
        );

        apiGameSystems.sort((a, b) => {
          if (a.manufacturerOrderNum === b.manufacturerOrderNum) {
            return a.gameSystemOrderNum - b.gameSystemOrderNum;
          } else {
            return a.manufacturerOrderNum - b.manufacturerOrderNum;
          }
        });

        setGameSystems(apiGameSystems);
      } catch (err) {
        postNotification("error", err);
      } finally {
        setLoading(false);
      }
    };
    go();
  }, []);

  useEffect(() => {
    if (gameSystem?.id !== undefined) {
      try {
        const fetchScoreCards = async () => {
          setScorecards(null);
          const apiScorecards = await handlePagingAll("/scorecards", {
            gameSystemId: gameSystem.id,
            type: "Pairing"
          });

          if (apiScorecards?.length) {
            setScorecards(
              apiScorecards.map((scoreCard) => {
                return {
                  label: scoreCard.name,
                  value: scoreCard.id,
                  scorecardType: scoreCard.scorecardType
                };
              })
            );
          } else {
            setScorecards([
              {
                value: "Default Scorecard",
                label: "Default Scorecard",
                default: true
              }
            ]);
          }
        };

        const fetchArmies = async () => {
          if (gameSystem?.hasFactions) {
            const { armyArray, subFactionDict } = await getArmyPickerArmies(
              gameSystem.id
            );

            if (armyArray?.length) {
              setArmies(armyArray);
              setArmyDict(subFactionDict);
            } else {
              setArmyDict(null);
              setHideFactions(true);
            }
          }
        };

        fetchScoreCards();
        fetchArmies();
      } catch (err) {
        postNotification("error", err);
      }
    }
  }, [gameSystem]);

  return (
    <>
      {(Boolean(loading) || Boolean(processing)) && <ModalLoadingFull />}

      <Box sx={{ width: "100%" }}>
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Grid container sx={{ gap: 2 }}>
            <Grid
              item
              xs={12}
              sx={{ p: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}
            >
              <Typography variant="h3">New Casual Game</Typography>
            </Grid>

            {gameSystems && (
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 1,
                  boxShadow: 1,
                  backgroundColor: "background.secondary"
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <GameSystemPicker
                      name="gameType"
                      value={gameSystem?.code || ""}
                      handleChange={(e) => {
                        if (e.target.value !== undefined) {
                          gameSystems.forEach((gs) => {
                            if (gs.code === e.target.value) {
                              setGameSystem(gs);
                              return true;
                            }
                          });
                        }
                      }}
                      gameSystems={gameSystems}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            {gameSystem && (
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 1,
                  boxShadow: 1,
                  backgroundColor: "background.secondary"
                }}
              >
                <Formik
                  initialValues={{
                    gameSystemId: gameSystem?.id,

                    pairingName: "",
                    scorecardId: gameSystem?.scorecardId || "",
                    pairingType: "CasualPairing",

                    player1: {
                      firstName:
                        authContext?.authState?.identity?.given_name || "",
                      lastName:
                        authContext?.authState?.identity?.family_name || "",
                      userId: authContext?.authState?.identity?.userId || null,
                      email: authContext?.authState?.identity?.email || "",

                      armyId: "",
                      subFactionId: ""
                    },

                    player2: {
                      firstName: "",
                      lastName: "",
                      userId: null,
                      email: "",

                      armyId: "",
                      subFactionId: ""
                    }
                  }}
                  validationSchema={Yup.object().shape({
                    pairingName: Yup.string()
                      .min(3, "Minimum 3 characters")
                      .max(255, "Maximum 255 characters")
                      .required("Required"),
                    scorecardId: Yup.string().required("Required"),

                    player2: Yup.object().shape({
                      firstName: Yup.string()
                        .max(255, "Maximum 255 characters")
                        .required("Required"),
                      lastName: Yup.string()
                        .max(255, "Maximum 255 characters")
                        .required("Required")
                    })
                  })}
                  enableReinitialize
                  onSubmit={async (values) => {
                    try {
                      setProcessing(true);

                      let sanitizedValues = {
                        ...values
                      };

                      if (sanitizedValues.player1?.armyId === "") {
                        delete sanitizedValues.player1.armyId;
                      }
                      if (
                        sanitizedValues.player1?.subFactionId === "" ||
                        sanitizedValues.player1?.subFactionId ===
                          "Select a parent faction"
                      ) {
                        delete sanitizedValues.player1.subFactionId;
                      }
                      if (sanitizedValues.player2?.armyId === "") {
                        delete sanitizedValues.player2.armyId;
                      }
                      if (
                        sanitizedValues.player2?.subFactionId === "" ||
                        sanitizedValues.player2?.subFactionId ===
                          "Select a parent faction"
                      ) {
                        delete sanitizedValues.player2.subFactionId;
                      }

                      if (
                        !sanitizedValues.scorecardId ||
                        sanitizedValues.scorecardId === "" ||
                        sanitizedValues.scorecardId === "Default Scorecard"
                      ) {
                        delete sanitizedValues.scorecardId;
                      }

                      const apiPairing = await postData(`/pairings`, {
                        body: {
                          ...sanitizedValues
                        }
                      });

                      postNotification("success", "Casual game created.");
                      navigate(`/game/casualpairing/${apiPairing.id}`);
                    } catch (err) {
                      postNotification("error", err);
                    }
                    setProcessing(false);
                  }}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    touched,
                    values,
                    setFieldValue
                  }) => (
                    <>
                      <form onSubmit={handleSubmit}>
                        <Grid container gap={1}>
                          <Grid item xs={12}>
                            <TextField
                              required
                              fullWidth
                              variant="outlined"
                              label="Game Title"
                              name="pairingName"
                              value={values.pairingName}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.pairingName && errors.pairingName
                              )}
                              helperText={
                                touched.pairingName && errors.pairingName
                              }
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <ScorecardPicker
                              name="scorecardId"
                              label="Scorecard"
                              required
                              value={values.scorecardId}
                              handleChange={handleChange}
                              scorecards={scorecards}
                              inclAny={false}
                              disabled={!scorecards}
                              error={Boolean(
                                touched.scorecardId && errors.scorecardId
                              )}
                              helperText={
                                touched.scorecardId && errors.scorecardId
                              }
                            />
                          </Grid>

                          <Grid item xs={12} sx={{ pt: 2 }}>
                            <Grid container spacing={3}>
                              {/* Player 1 column */}
                              <Grid item xs={12} sm={6}>
                                <Grid container spacing={1}>
                                  <Grid item xs={12}>
                                    <Typography variant="h4">
                                      Player 1
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      value={values.player1?.email}
                                      variant="outlined"
                                      label="Email"
                                      name="player1.email"
                                      disabled
                                      onChange={(e) => {
                                        const trimmedString = e.target.value
                                          .trim()
                                          .toLowerCase();
                                        setFieldValue(
                                          "player1.email",
                                          trimmedString
                                        );
                                      }}
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      value={values.player1?.firstName}
                                      variant="outlined"
                                      label="First Name"
                                      name="player1.firstName"
                                      disabled
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      value={values.player1?.lastName}
                                      variant="outlined"
                                      label="Last Name"
                                      name="player1.lastName"
                                      disabled
                                    />
                                  </Grid>

                                  {gameSystem.hasFactions && !hideFactions && (
                                    <Grid item xs={12}>
                                      <ArmyPicker
                                        armies={armies}
                                        name="player1.armyId"
                                        label={
                                          gameSystem?.factionLabel || "Faction"
                                        }
                                        value={values.player1?.armyId}
                                        handleChange={(e) => {
                                          handleChange(e);
                                          setFieldValue(
                                            "player1.subFactionId",
                                            ""
                                          );
                                        }}
                                        error={Boolean(
                                          touched.player1?.armyId &&
                                            errors.player1?.armyId
                                        )}
                                        helperText={
                                          touched.player1?.armyId &&
                                          errors.player1?.armyId
                                        }
                                      />
                                    </Grid>
                                  )}

                                  {gameSystem?.hasSubFactions &&
                                    armyDict?.[values.player1?.armyId] &&
                                    !hideFactions && (
                                      <Grid item xs={12}>
                                        <GenericPicker
                                          name="player1.subFactionId"
                                          label={
                                            gameSystem?.subFactionLabel ||
                                            "Sub Faction"
                                          }
                                          value={
                                            !values.player1?.armyId ||
                                            values.player1?.armyId === ""
                                              ? "Select a parent faction"
                                              : values.player1?.subFactionId
                                          }
                                          handleChange={handleChange}
                                          inclAny={false}
                                          list={
                                            !values.player1?.armyId ||
                                            values.player1?.armyId === ""
                                              ? ["Select a parent faction"]
                                              : armyDict?.[
                                                  values.player1?.armyId
                                                ]
                                          }
                                          error={Boolean(
                                            touched.player1?.subFactionId &&
                                              errors.player1?.subFactionId
                                          )}
                                          helperText={
                                            touched.player1?.subFactionId &&
                                            errors.player1?.subFactionId
                                          }
                                          disabled={
                                            !values.player1?.armyId ||
                                            values.player1?.armyId === ""
                                          }
                                        />
                                      </Grid>
                                    )}
                                </Grid>
                              </Grid>

                              {/* Player 2 column */}
                              <Grid item xs={12} sm={6}>
                                <Grid container spacing={1}>
                                  <Grid item xs={12}>
                                    <Typography variant="h4">
                                      Player 2
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      value={values.player2.email}
                                      variant="outlined"
                                      label="Email"
                                      name="player2.email"
                                      onChange={(e) => {
                                        const trimmedString = e.target.value
                                          .trim()
                                          .toLowerCase();
                                        setFieldValue(
                                          "player2.email",
                                          trimmedString
                                        );
                                      }}
                                      onBlur={async (e) => {
                                        handleBlur(e);
                                        if (e.target.value !== "") {
                                          setLoading(true);

                                          try {
                                            let apiUser = await getData(
                                              `/users/${e.target.value}`
                                            );

                                            if (apiUser) {
                                              setFieldValue(
                                                "player2.userId",
                                                apiUser.id
                                              );

                                              if (apiUser.firstName) {
                                                setFieldValue(
                                                  "player2.firstName",
                                                  apiUser.firstName
                                                );
                                              }

                                              if (apiUser.lastName) {
                                                setFieldValue(
                                                  "player2.lastName",
                                                  apiUser.lastName
                                                );
                                              }

                                              setLoading(false);
                                            } else {
                                              setFieldValue(
                                                "player2.userId",
                                                null
                                              );
                                            }
                                          } catch (err) {
                                            setFieldValue(
                                              "player2.userId",
                                              null
                                            );
                                            setLoading(false);
                                          }
                                        }
                                      }}
                                      error={Boolean(
                                        touched?.player2?.email &&
                                          errors?.player2?.email
                                      )}
                                      helperText={
                                        touched?.player2?.email &&
                                        errors?.player2?.email
                                      }
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      required
                                      fullWidth
                                      value={values.player2.firstName}
                                      variant="outlined"
                                      label="First Name"
                                      name="player2.firstName"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={Boolean(
                                        touched?.player2?.firstName &&
                                          errors?.player2?.firstName
                                      )}
                                      helperText={
                                        touched?.player2?.firstName &&
                                        errors?.player2?.firstName
                                      }
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      required
                                      fullWidth
                                      value={values.player2.lastName}
                                      variant="outlined"
                                      label="Last Name"
                                      name="player2.lastName"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={Boolean(
                                        touched?.player2?.lastName &&
                                          errors?.player2?.lastName
                                      )}
                                      helperText={
                                        touched?.player2?.lastName &&
                                        errors?.player2?.lastName
                                      }
                                    />
                                  </Grid>

                                  {gameSystem.hasFactions && !hideFactions && (
                                    <Grid item xs={12}>
                                      <ArmyPicker
                                        armies={armies}
                                        name="player2.armyId"
                                        label={
                                          gameSystem?.factionLabel || "Faction"
                                        }
                                        value={values.player2?.armyId}
                                        handleChange={(e) => {
                                          handleChange(e);
                                          setFieldValue(
                                            "player2.subFactionId",
                                            ""
                                          );
                                        }}
                                        error={Boolean(
                                          touched.player2?.armyId &&
                                            errors.player2?.armyId
                                        )}
                                        helperText={
                                          touched.player2?.armyId &&
                                          errors.player2?.armyId
                                        }
                                      />
                                    </Grid>
                                  )}

                                  {gameSystem?.hasSubFactions &&
                                    armyDict?.[values.player2?.armyId] &&
                                    !hideFactions && (
                                      <Grid item xs={12}>
                                        <GenericPicker
                                          name="player2.subFactionId"
                                          label={
                                            gameSystem?.subFactionLabel ||
                                            "Sub Faction"
                                          }
                                          value={
                                            !values.player2?.armyId ||
                                            values.player2?.armyId === ""
                                              ? "Select a parent faction"
                                              : values.player2?.subFactionId
                                          }
                                          handleChange={handleChange}
                                          inclAny={false}
                                          list={
                                            !values.player2?.armyId ||
                                            values.player2?.armyId === ""
                                              ? ["Select a parent faction"]
                                              : armyDict?.[
                                                  values.player2?.armyId
                                                ]
                                          }
                                          error={Boolean(
                                            touched.player2?.subFactionId &&
                                              errors.player2?.subFactionId
                                          )}
                                          helperText={
                                            touched.player2?.subFactionId &&
                                            errors.player2?.subFactionId
                                          }
                                          disabled={
                                            !values.player2?.armyId ||
                                            values.player2?.armyId === ""
                                          }
                                        />
                                      </Grid>
                                    )}
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>

                          {hideFactions && (
                            <Grid item xs={12} pt={2}>
                              <Typography variant="body2" textAlign="center">
                                No factions found
                              </Typography>
                            </Grid>
                          )}

                          <Grid item xs={12} sx={{ pt: 2 }}>
                            <Button
                              type="submit"
                              fullWidth
                              color="success"
                              variant="contained"
                            >
                              Create Game
                            </Button>
                          </Grid>
                        </Grid>
                      </form>
                    </>
                  )}
                </Formik>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
    </>
  );
}
